import type { MouseEvent } from 'react'
import { motion } from 'motion/react'
import { Globe2, Linkedin, Mail, Share2, Youtube } from 'lucide-react'
import { ASSET_ICON } from '../constants'

interface FooterProps {
  onSobre: () => void
  onEmpregos: () => void
}

const DESCOBRA = [
  { label: 'Vagas abertas', href: '#/', action: 'empregos' as const },
  { label: 'Sobre o Grupo HI', href: '#/sobre', action: 'sobre' as const },
  { label: 'Empresas do grupo', href: '#/sobre', action: 'sobre' as const },
  { label: 'Área RH', href: '/login', action: null },
]

const MISSAO = [
  { label: 'Quem somos', href: '#/sobre', action: 'sobre' as const },
  { label: 'Ecossistema de marcas', href: '#/sobre', action: 'sobre' as const },
  { label: 'Carreiras', href: '#/', action: 'empregos' as const },
  { label: 'Junte-se à equipe', href: '#/', action: 'empregos' as const },
]

const CONCIERGE = [
  { label: 'Entre em contato', href: 'mailto:rh@grupohi.com.br', action: null },
  { label: 'Privacidade', href: '#', action: null },
  { label: 'Termos de uso', href: '#', action: null },
  { label: 'Reportar problema', href: 'mailto:rh@grupohi.com.br', action: null },
]

const SOCIAL = [
  { Icon: Share2, label: 'Compartilhar', href: '#' },
  { Icon: Globe2, label: 'Site', href: 'https://grupohi.com.br/' },
  { Icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com' },
  { Icon: Youtube, label: 'YouTube', href: '#' },
  { Icon: Mail, label: 'E-mail', href: 'mailto:rh@grupohi.com.br' },
] as const

export function Footer({ onSobre, onEmpregos }: FooterProps) {
  const handleNav = (
    e: MouseEvent,
    action: 'sobre' | 'empregos' | null,
    href: string,
  ) => {
    if (!action) return
    e.preventDefault()
    if (action === 'sobre') onSobre()
    else onEmpregos()
    if (href.startsWith('#')) {
      history.replaceState(null, '', href)
    }
  }

  return (
    <div className="cr-footer-shell relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-10">
      <motion.footer
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 0.4, ease: 'easeOut' }}
        className="liquid-glass cr-footer-glass cr-glass-float w-full rounded-3xl p-6 md:p-10 text-white/70 mt-16 md:mt-24"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 mb-10">
          <div className="md:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <span className="cr-emblem-plate cr-emblem-plate--sm bg-white/10 rounded-xl p-1.5">
                <img src={ASSET_ICON} width={40} height={40} alt="" />
              </span>
              <span className="text-xl font-medium text-white">
                Grupo <span className="text-[var(--hi-green,#00d931)]">HI</span>
              </span>
            </div>
            <p className="text-sm leading-relaxed max-w-sm">
              Transformamos tecnologia em eficiência. Ecossistema de franquias, tecnologia, máquinas e
              higiene — venha fazer parte dessa revolução.
            </p>
          </div>

          <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8">
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white font-medium mb-4">
                Descubra
              </h3>
              <ul className="text-xs space-y-2">
                {DESCOBRA.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="hover:text-white transition-colors"
                      onClick={(e) => handleNav(e, item.action, item.href)}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white font-medium mb-4">
                A Missão
              </h3>
              <ul className="text-xs space-y-2">
                {MISSAO.map((item) => (
                  <li key={item.label}>
                    <a
                      href={item.href}
                      className="hover:text-white transition-colors"
                      onClick={(e) => handleNav(e, item.action, item.href)}
                    >
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h3 className="text-sm uppercase tracking-wider text-white font-medium mb-4">
                Serviço
              </h3>
              <ul className="text-xs space-y-2">
                {CONCIERGE.map((item) => (
                  <li key={item.label}>
                    <a href={item.href} className="hover:text-white transition-colors">
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-6 md:gap-4">
          <p className="text-[10px] uppercase tracking-widest opacity-50">
            © {new Date().getFullYear()} Grupo HI · Cabedelo / João Pessoa — PB
          </p>
          <div className="flex items-center gap-4">
            <span className="text-[10px] uppercase tracking-widest opacity-50">Siga o grupo:</span>
            <div className="flex items-center gap-3">
              {SOCIAL.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  aria-label={label}
                  target={href.startsWith('http') ? '_blank' : undefined}
                  rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                  className="opacity-70 hover:opacity-100 transition-colors hover:text-white"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </motion.footer>
    </div>
  )
}
